import React, { useMemo, useState } from 'react';
import { ClinicTouchCanvas } from './ClinicTouchCanvas';
import { ClinicProcedureTemplateCanvas } from './ClinicProcedureTemplateCanvas';

const initialForm = {
  patient_name: '',
  patient_phone: '',
  patient_email: '',
  chief_complaint: '',
  assessment: '',
  treatment_plan: '',
  nurse_notes: '',
  post_treatment_notes: '',
  follow_up_date: '',
  canvas_image_data_url: '',
  signature_data_url: '',
  template_state: { version: 1, pages: {} },
  status: 'abierto',
};

export const ClinicHistoriesPanel = ({ workspace, onCreateHistory, onUpdateHistory, saving, canEdit, consultMode, onDownloadPdf }) => {
  const [form, setForm] = useState(initialForm);
  const [editingHistoryId, setEditingHistoryId] = useState('');
  const [selectedProductId, setSelectedProductId] = useState('');
  const [selectedQuantity, setSelectedQuantity] = useState(1);
  const [prescribedItems, setPrescribedItems] = useState([]);
  const [historyQuery, setHistoryQuery] = useState('');
  const [visibleCount, setVisibleCount] = useState(40);

  const inventoryMap = useMemo(
    () => new Map((workspace.inventory_items || []).map((item) => [item.id, item])),
    [workspace.inventory_items],
  );

  const filteredHistories = useMemo(() => {
    const needle = historyQuery.trim().toLowerCase();
    if (!needle) return workspace.clinical_histories || [];
    return (workspace.clinical_histories || []).filter((history) => [
      history.patient_name,
      history.chief_complaint,
      history.assessment,
      history.treatment_plan,
      history.legacy_site,
      history.legacy_assistant,
      history.legacy_latest_annotation,
    ].filter(Boolean).join(' ').toLowerCase().includes(needle));
  }, [historyQuery, workspace.clinical_histories]);

  const visibleHistories = filteredHistories.slice(0, visibleCount);

  const addProduct = () => {
    const product = inventoryMap.get(selectedProductId);
    if (!product) return;
    setPrescribedItems((prev) => [...prev, { inventory_item_id: product.id, product_name: product.name, quantity: selectedQuantity }]);
    setSelectedProductId('');
    setSelectedQuantity(1);
  };

  const removeProduct = (index) => {
    setPrescribedItems((prev) => prev.filter((_, currentIndex) => currentIndex !== index));
  };

  const loadHistoryIntoForm = (history) => {
    setEditingHistoryId(history.id);
    setForm({
      patient_name: history.patient_name || '',
      patient_phone: history.patient_phone || '',
      patient_email: history.patient_email || '',
      chief_complaint: history.chief_complaint || '',
      assessment: history.assessment || '',
      treatment_plan: history.treatment_plan || '',
      nurse_notes: history.nurse_notes || '',
      post_treatment_notes: history.post_treatment_notes || '',
      follow_up_date: history.follow_up_date ? history.follow_up_date.slice(0, 16) : '',
      canvas_image_data_url: history.canvas_image_url || '',
      signature_data_url: history.signature_image_url || '',
      template_state: history.template_state || { version: 1, pages: {} },
      status: history.status || 'abierto',
    });
    setPrescribedItems(history.prescribed_items || []);
  };

  const resetEditor = () => {
    setEditingHistoryId('');
    setForm(initialForm);
    setPrescribedItems([]);
    setSelectedProductId('');
    setSelectedQuantity(1);
  };

  const submit = async (event) => {
    event.preventDefault();
    const payload = {
      ...form,
      prescribed_items: prescribedItems,
      template_state: form.template_state,
      template_label: workspace.clinical_master_template?.name,
      template_source_url: workspace.clinical_master_template?.pdf_url,
      template_page_count: workspace.clinical_master_template?.page_count,
    };
    const success = editingHistoryId ? await onUpdateHistory(editingHistoryId, payload) : await onCreateHistory(payload);
    if (success) {
      resetEditor();
    }
  };

  return (
    <section className="grid gap-5 xl:grid-cols-[0.95fr_1.05fr]" data-testid="clinic-histories-panel">
      <form onSubmit={submit} className="rounded-[28px] border border-white/60 bg-white/95 p-5 shadow-[0_20px_60px_rgba(15,23,42,0.08)] sm:p-6" data-testid="clinic-history-form">
        <p className="text-xs uppercase tracking-[0.24em] text-[#0f766e]">Historial completamente interactivo</p>
        <h2 className="mt-2 text-2xl font-black text-slate-900">{editingHistoryId ? 'Editar historial clínico' : 'Nueva evolución clínica'}</h2>
        <div className="mt-3 rounded-[22px] bg-[#f1f5f9] px-4 py-3 text-sm text-slate-600" data-testid="clinic-history-consult-mode-banner">
          {consultMode ? 'Modo consulta táctil activo: pensado para tablet, dibujo, firma y prescripción rápida.' : 'Vista completa para escritorio. Puedes activar el modo consulta para tablet desde la cabecera.'}
        </div>
        <div className="mt-3 grid gap-3 md:grid-cols-3" data-testid="clinic-history-simple-steps">
          <div className="rounded-[20px] bg-white px-4 py-3 text-sm text-slate-600"><span className="font-bold text-slate-900">1.</span> Escribe los datos básicos del paciente.</div>
          <div className="rounded-[20px] bg-white px-4 py-3 text-sm text-slate-600"><span className="font-bold text-slate-900">2.</span> Marca en la imagen del cuerpo, añade pines, flechas o notas.</div>
          <div className="rounded-[20px] bg-white px-4 py-3 text-sm text-slate-600"><span className="font-bold text-slate-900">3.</span> Guarda y vuelve a abrir el historial cuando quieras para seguir editando.</div>
        </div>
        {editingHistoryId && (
          <div className="mt-3 flex flex-wrap gap-2" data-testid="clinic-history-editing-banner">
            <span className="rounded-full bg-amber-100 px-4 py-2 text-xs font-semibold text-amber-700">Reabriendo historial para seguir editando</span>
            <button type="button" onClick={resetEditor} className="rounded-full bg-slate-100 px-4 py-2 text-xs font-semibold text-slate-700" data-testid="clinic-history-cancel-edit-button">
              Cancelar edición
            </button>
          </div>
        )}
        <div className="mt-5 space-y-4">
          <input data-testid="clinic-history-patient-input" className="w-full rounded-2xl border border-slate-200 px-4 py-3" placeholder="Paciente" value={form.patient_name} onChange={(event) => setForm((prev) => ({ ...prev, patient_name: event.target.value }))} required />
          <div className="grid gap-4 sm:grid-cols-2">
            <input data-testid="clinic-history-phone-input" className="rounded-2xl border border-slate-200 px-4 py-3" placeholder="Teléfono" value={form.patient_phone} onChange={(event) => setForm((prev) => ({ ...prev, patient_phone: event.target.value }))} />
            <input data-testid="clinic-history-email-input" type="email" className="rounded-2xl border border-slate-200 px-4 py-3" placeholder="Email" value={form.patient_email} onChange={(event) => setForm((prev) => ({ ...prev, patient_email: event.target.value }))} />
          </div>
          <textarea data-testid="clinic-history-chief-complaint-input" className="min-h-[90px] w-full rounded-2xl border border-slate-200 px-4 py-3" placeholder="Motivo de consulta" value={form.chief_complaint} onChange={(event) => setForm((prev) => ({ ...prev, chief_complaint: event.target.value }))} required />
          <textarea data-testid="clinic-history-assessment-input" className="min-h-[110px] w-full rounded-2xl border border-slate-200 px-4 py-3" placeholder="Valoración clínica" value={form.assessment} onChange={(event) => setForm((prev) => ({ ...prev, assessment: event.target.value }))} required />
          <textarea data-testid="clinic-history-treatment-plan-input" className="min-h-[110px] w-full rounded-2xl border border-slate-200 px-4 py-3" placeholder="Plan terapéutico" value={form.treatment_plan} onChange={(event) => setForm((prev) => ({ ...prev, treatment_plan: event.target.value }))} required />
          <textarea data-testid="clinic-history-nurse-notes-input" className="min-h-[90px] w-full rounded-2xl border border-slate-200 px-4 py-3" placeholder="Notas de enfermería" value={form.nurse_notes} onChange={(event) => setForm((prev) => ({ ...prev, nurse_notes: event.target.value }))} />
          <textarea data-testid="clinic-history-post-treatment-notes-input" className="min-h-[90px] w-full rounded-2xl border border-slate-200 px-4 py-3" placeholder="Seguimiento / cuidados post-tratamiento" value={form.post_treatment_notes} onChange={(event) => setForm((prev) => ({ ...prev, post_treatment_notes: event.target.value }))} />
          <input data-testid="clinic-history-follow-up-date-input" type="datetime-local" className="w-full rounded-2xl border border-slate-200 px-4 py-3" value={form.follow_up_date} onChange={(event) => setForm((prev) => ({ ...prev, follow_up_date: event.target.value }))} />

          <ClinicProcedureTemplateCanvas
            template={workspace.clinical_master_template}
            value={form.canvas_image_data_url}
            stateValue={form.template_state}
            onChange={(canvasValue) => setForm((prev) => ({ ...prev, canvas_image_data_url: canvasValue }))}
            onStateChange={(templateState) => setForm((prev) => ({ ...prev, template_state: templateState }))}
            testId="clinic-history-canvas"
            clearTestId="clinic-history-canvas-clear-button"
          />

          <ClinicTouchCanvas
            label="Firma de la consulta"
            helper="Firma rápida para validación clínica en tablet o PC."
            value={form.signature_data_url}
            onChange={(signatureValue) => setForm((prev) => ({ ...prev, signature_data_url: signatureValue }))}
            testId="clinic-history-signature-canvas"
            clearTestId="clinic-history-signature-clear-button"
            variant="signature"
          />

          <div className="rounded-[24px] border border-slate-200 bg-[#f8fafc] p-4" data-testid="clinic-history-prescriptions-box">
            <p className="text-sm font-semibold text-slate-900">Deducción automática real de stock</p>
            <div className="mt-3 grid gap-3 sm:grid-cols-[1fr_120px_auto]">
              <select data-testid="clinic-history-product-select" className="rounded-2xl border border-slate-200 px-4 py-3" value={selectedProductId} onChange={(event) => setSelectedProductId(event.target.value)}>
                <option value="">Selecciona producto</option>
                {workspace.inventory_items.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
              </select>
              <input data-testid="clinic-history-product-quantity-input" type="number" min="1" className="rounded-2xl border border-slate-200 px-4 py-3" value={selectedQuantity} onChange={(event) => setSelectedQuantity(Number(event.target.value))} />
              <button type="button" onClick={addProduct} className="rounded-full bg-slate-900 px-5 py-3 text-sm font-semibold text-white" data-testid="clinic-history-add-product-button">Añadir</button>
            </div>
            <div className="mt-3 space-y-2" data-testid="clinic-history-prescribed-items-list">
              {prescribedItems.length ? prescribedItems.map((item, index) => (
                <div key={`${item.inventory_item_id}-${index}`} className="flex items-center justify-between rounded-2xl bg-white px-4 py-3 text-sm text-slate-700" data-testid={`clinic-history-prescribed-item-${index}`}>
                  <span>{item.product_name}</span>
                  <div className="flex items-center gap-3">
                    <span>{item.quantity} uds</span>
                    <button type="button" onClick={() => removeProduct(index)} className="rounded-full bg-rose-50 px-3 py-1 text-xs font-semibold text-rose-600" data-testid={`clinic-history-prescribed-remove-${index}`}>Quitar</button>
                  </div>
                </div>
              )) : <p className="text-sm text-slate-500" data-testid="clinic-history-prescribed-empty">Añade productos y el backend descontará stock real al guardar el historial.</p>}
            </div>
          </div>

          <button data-testid="clinic-history-submit-button" type="submit" className={`w-full rounded-full bg-slate-900 px-5 py-3 font-semibold text-white transition hover:bg-slate-700 ${consultMode ? 'text-base' : 'text-sm'}`} disabled={(saving === 'history-create' || saving === `history-update-${editingHistoryId}`) || !canEdit}>
            {saving === 'history-create' || saving === `history-update-${editingHistoryId}` ? 'Guardando…' : editingHistoryId ? 'Guardar cambios del historial' : 'Guardar historial'}
          </button>
        </div>
      </form>

      <div className="space-y-4" data-testid="clinic-histories-list">
        <div className="rounded-[24px] border border-slate-200 bg-white/90 p-4" data-testid="clinic-histories-search-box">
          <input
            data-testid="clinic-histories-search-input"
            className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm"
            placeholder="Buscar por paciente, sede, asistente, tratamiento o anotación"
            value={historyQuery}
            onChange={(event) => { setHistoryQuery(event.target.value); setVisibleCount(40); }}
          />
          <p className="mt-2 text-xs text-slate-500" data-testid="clinic-histories-search-count">Mostrando {visibleHistories.length} de {filteredHistories.length} historial(es)</p>
        </div>
        {visibleHistories.length ? visibleHistories.map((history) => (
          <article key={history.id} className="rounded-[28px] border border-white/60 bg-white/95 p-5 shadow-[0_16px_40px_rgba(15,23,42,0.06)]" data-testid={`clinic-history-item-${history.id}`}>
            <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
              <div>
                <p className="text-lg font-bold text-slate-900" data-testid={`clinic-history-name-${history.id}`}>{history.patient_name}</p>
                <p className="mt-1 text-sm text-slate-600">{history.chief_complaint}</p>
                <p className="mt-2 text-sm text-slate-500">{history.treatment_plan}</p>
              </div>
              <div className="flex flex-wrap gap-2">
                <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600" data-testid={`clinic-history-status-${history.id}`}>{history.status}</span>
                <span className={`rounded-full px-3 py-1 text-xs font-semibold ${history.stock_deduction_status === 'applied' ? 'bg-violet-100 text-violet-700' : 'bg-slate-100 text-slate-500'}`} data-testid={`clinic-history-stock-status-${history.id}`}>{history.stock_deduction_status === 'applied' ? 'stock descontado' : 'sin descuento'}</span>
              </div>
            </div>
            <div className="mt-4 grid gap-3 sm:grid-cols-2" data-testid={`clinic-history-import-meta-${history.id}`}>
              <div className="rounded-[22px] bg-[#f8fafc] p-4" data-testid={`clinic-history-session-meta-${history.id}`}>
                <p className="text-xs uppercase tracking-[0.2em] text-slate-400">Sesión importada</p>
                <p className="mt-2 text-sm font-semibold text-slate-900">{history.updated_at ? new Date(history.updated_at).toLocaleString('es-ES') : 'Sin fecha'}</p>
                <p className="mt-1 text-xs text-slate-500">{history.legacy_site || 'sin sede'} · {history.legacy_assistant || 'sin asistente'} · {history.legacy_rows_count || 0} movimiento(s)</p>
              </div>
              <div className="rounded-[22px] bg-[#f8fafc] p-4" data-testid={`clinic-history-annotation-meta-${history.id}`}>
                <p className="text-xs uppercase tracking-[0.2em] text-slate-400">Última anotación</p>
                <p className="mt-2 text-sm font-semibold text-slate-900">{history.legacy_latest_annotation || history.post_treatment_notes || 'Sin anotación clínica'}</p>
                <p className="mt-1 text-xs text-slate-500">Importe histórico {history.legacy_amount_total || 0} · pago {history.legacy_payment_type || 'sin dato'}</p>
                {!!history.template_state_summary && <p className="mt-2 text-xs text-[#0f766e]" data-testid={`clinic-history-template-summary-${history.id}`}>Campos {history.template_state_summary.fields_completed || 0} · marcadores {history.template_state_summary.markers || 0} · páginas {history.template_state_summary.pages_touched || 0}</p>}
              </div>
            </div>
            <div className="mt-4 grid gap-3 sm:grid-cols-2" data-testid={`clinic-history-assets-${history.id}`}>
              <div className="rounded-[22px] bg-[#f8fafc] p-4" data-testid={`clinic-history-followup-${history.id}`}>
                <p className="text-xs uppercase tracking-[0.2em] text-slate-400">Seguimiento</p>
                <p className="mt-2 text-sm font-semibold text-slate-900">{history.follow_up_date ? new Date(history.follow_up_date).toLocaleString('es-ES') : 'Sin fecha'}</p>
                <p className={`mt-1 text-xs ${history.follow_up_overdue ? 'text-rose-600' : 'text-slate-500'}`}>{history.follow_up_overdue ? 'Vencido' : 'En tiempo'}</p>
                {!!history.template_label && <p className="mt-2 text-xs text-[#0f766e]" data-testid={`clinic-history-template-label-${history.id}`}>{history.template_label} · {history.template_page_count || 0} pág.</p>}
                {!!history.template_source_url && <a href={history.template_source_url} target="_blank" rel="noreferrer" className="mt-2 inline-block text-xs font-semibold text-[#075985]" data-testid={`clinic-history-template-link-${history.id}`}>Abrir plantilla maestra</a>}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="overflow-hidden rounded-[22px] border border-slate-200 bg-white" data-testid={`clinic-history-canvas-preview-${history.id}`}>
                  {history.canvas_image_url ? <img src={history.canvas_image_url} alt={`dibujo ${history.patient_name}`} className="h-full w-full object-cover" /> : <div className="flex h-full min-h-[96px] items-center justify-center text-xs text-slate-400">Sin dibujo</div>}
                </div>
                <div className="overflow-hidden rounded-[22px] border border-slate-200 bg-white" data-testid={`clinic-history-signature-preview-${history.id}`}>
                  {history.signature_image_url ? <img src={history.signature_image_url} alt={`firma ${history.patient_name}`} className="h-full w-full object-contain" /> : <div className="flex h-full min-h-[96px] items-center justify-center text-xs text-slate-400">Sin firma</div>}
                </div>
              </div>
            </div>
            <div className="mt-4 rounded-[22px] bg-[#f8fafc] p-4" data-testid={`clinic-history-stock-preview-${history.id}`}>
              <p className="text-sm font-semibold text-slate-900">Preview de stock</p>
              <div className="mt-3 space-y-2">
                {history.stock_preview?.items?.length ? history.stock_preview.items.map((item, index) => (
                  <div key={`${history.id}-${index}`} className="flex items-center justify-between text-sm text-slate-700" data-testid={`clinic-history-stock-preview-item-${history.id}-${index}`}>
                    <span>{item.product_name}</span>
                    <span>{item.shortage ? 'Sin stock suficiente' : item.deduction_applied ? `descontado · ${item.current_stock} actuales` : `${item.remaining_units_after_deduction} restantes`}</span>
                  </div>
                )) : <p className="text-sm text-slate-500">Sin productos vinculados todavía.</p>}
              </div>
            </div>
            {canEdit && (
              <div className="mt-4 flex flex-wrap gap-2">
                <button type="button" onClick={() => loadHistoryIntoForm(history)} className="rounded-full bg-slate-900 px-4 py-2 text-sm font-semibold text-white" data-testid={`clinic-history-edit-interactive-${history.id}`}>Abrir editor interactivo</button>
                {!!onDownloadPdf && (
                  <button type="button" onClick={() => onDownloadPdf(history)} className="rounded-full bg-[#0f766e] px-4 py-2 text-sm font-semibold text-white disabled:opacity-50" disabled={saving === `pdf-${history.id}`} data-testid={`clinic-history-download-pdf-${history.id}`}>
                    {saving === `pdf-${history.id}` ? 'Generando…' : 'Descargar PDF'}
                  </button>
                )}
                {history.status !== 'cerrado' && <button type="button" onClick={() => onUpdateHistory(history.id, { status: 'en_revision' })} className="rounded-full bg-[#dbeafe] px-4 py-2 text-sm font-semibold text-[#1d4ed8]" data-testid={`clinic-history-review-${history.id}`}>Pasar a revisión</button>}
                {history.status !== 'cerrado' && <button type="button" onClick={() => onUpdateHistory(history.id, { status: 'cerrado' })} className="rounded-full bg-[#dcfce7] px-4 py-2 text-sm font-semibold text-[#166534]" data-testid={`clinic-history-close-${history.id}`}>Cerrar</button>}
              </div>
            )}
          </article>
        )) : <div className="rounded-[28px] border border-dashed border-slate-300 bg-white/80 p-8 text-sm text-slate-500" data-testid="clinic-histories-empty">Todavía no hay historiales creados.</div>}
        {filteredHistories.length > visibleCount && (
          <div className="flex justify-center">
            <button type="button" onClick={() => setVisibleCount((count) => count + 40)} className="rounded-full bg-white px-5 py-3 text-sm font-semibold text-slate-700 shadow-sm" data-testid="clinic-histories-load-more-button">
              Mostrar 40 más
            </button>
          </div>
        )}
      </div>
    </section>
  );
};

